// The verdict on a customer's share link for USB or disc content (ADR-021 §4),
// shown under the link field the moment it is pasted: whether we can download
// it, and if not, exactly what to change on that provider. A link that works is
// copied into our own storage before it can expire; its progress shows here.

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MEDIA_CONTENT_LINK_PATTERN,
  MEDIA_CONTENT_RETENTION_DAYS,
  type MediaContentQuoteStateResponse,
  type MediaLinkCheckDto,
} from 'shared';

import { extractApiError } from '../api/client.js';
import * as mediaContentApi from '../api/media-content.js';
import { formatDecimalBytes, formatNumber } from '../i18n/formatters.js';
import {
  mediaContentLineState,
  mediaContentStateKey,
  useMediaContentQuoteState,
} from './useMediaContentState.js';

const LINK_PATTERN = new RegExp(MEDIA_CONTENT_LINK_PATTERN);

function linkMessageKeys(check: MediaLinkCheckDto): string[] {
  const keys = [
    `mediaContent.link.${check.status}.${check.provider}`,
    `mediaContent.link.${check.status}.default`,
  ];
  return check.message_key ? [check.message_key, ...keys] : keys;
}

export function MediaContentLinkCheck({
  quoteId,
  quoteLineId,
  url,
  inputDomId,
  disabled,
}: {
  quoteId: string;
  quoteLineId: string;
  url: string;
  inputDomId: string;
  disabled: boolean;
}): JSX.Element | null {
  const { t } = useTranslation('quote');
  const qc = useQueryClient();
  const stateQuery = useMediaContentQuoteState(quoteId);
  const stored = mediaContentLineState(stateQuery.data, quoteLineId)?.latest_link_check ?? null;
  const [checking, setChecking] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const trimmed = url.trim();
  const valid = LINK_PATTERN.test(trimmed);
  const current = stored && stored.url === trimmed ? stored : null;

  const latestRef = useRef({
    trimmed,
    valid,
    checkedUrl: current?.url ?? null,
    checking,
    disabled,
  });
  latestRef.current = { trimmed, valid, checkedUrl: current?.url ?? null, checking, disabled };

  async function runCheck(target: string): Promise<void> {
    setChecking(true);
    setRequestError(null);
    try {
      const check = await mediaContentApi.checkMediaContentLink({
        quote_id: quoteId,
        quote_line_id: quoteLineId,
        url: target,
      });
      qc.setQueryData<MediaContentQuoteStateResponse>(mediaContentStateKey(quoteId), (old) => {
        const lines = old?.lines ?? [];
        const found = lines.some((l) => l.quote_line_id === quoteLineId);
        return {
          lines: found
            ? lines.map((l) =>
                l.quote_line_id === quoteLineId ? { ...l, latest_link_check: check } : l
              )
            : [
                ...lines,
                { quote_line_id: quoteLineId, latest_session: null, latest_link_check: check },
              ],
        };
      });
      void qc.invalidateQueries({ queryKey: mediaContentStateKey(quoteId) });
    } catch (err) {
      setRequestError(extractApiError(err)?.message ?? t('mediaContent.link.requestFailed'));
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    const input = document.getElementById(inputDomId);
    if (!input) return;
    const onBlur = (): void => {
      const s = latestRef.current;
      if (s.disabled || s.checking || !s.valid || s.checkedUrl === s.trimmed) return;
      void runCheck(s.trimmed);
    };
    input.addEventListener('blur', onBlur);
    return () => input.removeEventListener('blur', onBlur);
    // runCheck closes over stable props; the live values are read through latestRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDomId, quoteId, quoteLineId]);

  if (disabled && !current) return null;

  return (
    <div className="media-link">
      {checking ? (
        <p className="field-tip" role="status">
          {t('mediaContent.link.checking')}
        </p>
      ) : current ? (
        <LinkVerdict check={current} />
      ) : null}
      {requestError && (
        <p className="field-error" role="alert">
          {requestError}
        </p>
      )}
      {!disabled && valid && !checking && (
        <button type="button" className="link" onClick={() => void runCheck(trimmed)}>
          {current ? t('mediaContent.link.checkAgain') : t('mediaContent.link.check')}
        </button>
      )}
    </div>
  );
}

function LinkVerdict({ check }: { check: MediaLinkCheckDto }): JSX.Element {
  const { t } = useTranslation('quote');
  const size = check.size_bytes !== null ? formatDecimalBytes(check.size_bytes) : null;
  const params = { size: size ?? '', filename: check.filename ?? '' };

  if (check.status === 'ok') {
    if (check.ingest_status === 'complete') {
      return (
        <p className="field-tip" role="status">
          {t('mediaContent.link.ingest.complete', {
            size: size ?? '',
            days: MEDIA_CONTENT_RETENTION_DAYS,
          })}
        </p>
      );
    }
    if (check.ingest_status === 'failed') {
      return (
        <p className="field-error" role="alert">
          {t('mediaContent.link.ingest.failed')}
        </p>
      );
    }
    const pct =
      check.size_bytes && check.ingested_bytes !== null
        ? Math.min(100, Math.floor((check.ingested_bytes / check.size_bytes) * 100))
        : null;
    return (
      <div className="field-tip">
        <p className="media-link__lead" role="status">
          {size
            ? t('mediaContent.link.ok.withSize', params)
            : t('mediaContent.link.ok.default', params)}
        </p>
        {pct !== null && (
          <>
            <div
              className="media-content__bar"
              role="progressbar"
              aria-label={t('mediaContent.link.ingest.progressAria')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div className="media-content__bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="help">
              {t('mediaContent.link.ingest.progress', { percent: formatNumber(pct) })}
            </span>
          </>
        )}
      </div>
    );
  }

  const message = t(linkMessageKeys(check), params);
  if (check.status === 'unsupported') {
    return (
      <p className="field-warning" role="status">
        {message}
      </p>
    );
  }
  return (
    <p className="field-error" role="alert">
      {message}
    </p>
  );
}
